'use client'

import { Skeleton } from '@mantine/core'
import dynamic from 'next/dynamic'
import FaqAccordion from '@/screens/dashboard/ui/FaqAccordion'
import AmenitiesSection from './section/AmenitiesSection'
import DescriptionSection from './section/DescriptionSection'
import MobileAmenitiesSection from './section/MobileAmenitiesSection'
import MobilePropertyDetailsCard from './section/MobilePropertyDetailsCard'
import MobileBookingForm from './section/MobileBokingForm'
import DesktopPropertyDetailsSummaryContent from './section/DesktopPropertyDetailsSummaryContent'
import ReviewsDesktop from './section/ReviewsDesktop'
import ReviewsMobile from './section/ReviewsMobile'

const ClientMapComponent = dynamic(
  () => import('@/screens/dashboard/ui/MapComponent'),
  { ssr: false, loading: () => <Skeleton height={300} radius='md' /> },
)

const NewBookingForm = dynamic(
  () => import('./section/NewBookingFrom'),
  { ssr: false },
)

function getLocationData(propertiesDetailsData) {
  return [
    {
      lat: propertiesDetailsData?.data?.lat,
      lng: propertiesDetailsData?.data?.lng,
      price: +propertiesDetailsData?.data?.daily_rate,
    },
  ]
}

function PropertyMapSection({
  id,
  propertiesDetailsLoading,
  propertiesDetailsData,
  sectionClassName,
  containerClassName,
  skeletonClassName,
  skeletonProps = {},
}) {
  return (
    <section id={id} className={sectionClassName}>
      <div className={containerClassName}>
        {propertiesDetailsLoading || !propertiesDetailsData ? (
          <Skeleton
            visible={propertiesDetailsLoading}
            className={skeletonClassName}
            {...skeletonProps}
          />
        ) : (
          <ClientMapComponent locationData={getLocationData(propertiesDetailsData)} />
        )}
      </div>
    </section>
  )
}

export function MobilePropertyDetailsContent({
  id,
  slug,
  initialGuests,
  initialCheckin,
  initialCheckout,
  propertiesDetailsData,
  propertiesDetailsLoading,
  showGuestsDropdownMobile,
  setShowGuestsDropdownMobile,
  isLong,
  words,
  isReviewsLoading,
  reviewsData,
  reviewCategoriesMobile,
  platformDataMobile,
  skeletonData,
  totalReviewCount,
}) {
  return (
    <div className='md:hidden pb-7'>
      {propertiesDetailsLoading || !propertiesDetailsData ? (
        <div className='w-full rounded-xl overflow-hidden px-4 mb-5'>
          <Skeleton height={300} className='w-full' />
        </div>
      ) : (
        <MobilePropertyDetailsCard
          data={propertiesDetailsData?.data}
          loading={propertiesDetailsLoading}
          slug={slug}
        />
      )}

      <MobileBookingForm
        id={id}
        data={propertiesDetailsData}
        loading={propertiesDetailsLoading}
        initialGuests={initialGuests}
        initialCheckin={initialCheckin}
        initialCheckout={initialCheckout}
        showGuestsDropdown={showGuestsDropdownMobile}
        setShowGuestsDropdown={setShowGuestsDropdownMobile}
      />

      <div className='px-3 py-2'>
        <DescriptionSection
          loading={propertiesDetailsLoading}
          data={propertiesDetailsData}
          isLong={isLong}
          words={words}
        />

        <PropertyMapSection
          id='propertyMap'
          propertiesDetailsLoading={propertiesDetailsLoading}
          propertiesDetailsData={propertiesDetailsData}
          sectionClassName='md:hidden my-6 mx-3'
          containerClassName='mt-5 w-full rounded-[19px] h-[305px] overflow-hidden mb-8'
          skeletonClassName='w-full h-[350px] rounded-xl overflow-hidden'
          skeletonProps={{
            height: 350,
            width: '100%',
            radius: 'xl',
            overflow: 'hidden',
          }}
        />

        <div className='md:hidden w-full mx-auto overflow-hidden ml-2'>
          <MobileAmenitiesSection
            data={propertiesDetailsData}
            loading={propertiesDetailsLoading}
          />
        </div>

        <ReviewsMobile
          loadingReviews={isReviewsLoading}
          reviewsDataNew={reviewsData}
          reviewCategoriesMobile={reviewCategoriesMobile}
          platformDataMobile={platformDataMobile}
          skeletonData={skeletonData}
          totalCount={totalReviewCount}
          id={id}
        />
      </div>
      <hr className='text-[#00000033] md:block hidden' />
    </div>
  )
}

export function DesktopPropertyDetailsContent({
  id,
  propertiesDetailsData,
  propertiesDetailsLoading,
  words,
  isLong,
  propertyDetails,
  isReviewsLoading,
  reviewsData,
  totalReviewCount,
  reviewCategories,
  platformData,
  skeletonData,
}) {
  return (
    <>
      <DesktopPropertyDetailsSummaryContent
        loading={propertiesDetailsLoading}
        data={propertiesDetailsData}
        words={words}
        isLong={isLong}
        propertyDetails={propertyDetails}
      />

      <PropertyMapSection
        id='propertyMap'
        propertiesDetailsLoading={propertiesDetailsLoading}
        propertiesDetailsData={propertiesDetailsData}
        sectionClassName='max-md:hidden'
        containerClassName='mt-5 w-full rounded-[19px] h-[305px] overflow-hidden md:mb-16 mb-8 mx-2.5 md:mx-0'
        skeletonClassName='md:w-[739px] w-[358px] rounded-[19px]'
        skeletonProps={{ h: 434 }}
      />

      <div className='max-md:hidden'>
        <AmenitiesSection
          data={propertiesDetailsData}
          loading={propertiesDetailsLoading}
        />
      </div>

      <ReviewsDesktop
        reviewsDataNew={reviewsData}
        loadingReviews={isReviewsLoading}
        totalCount={totalReviewCount}
        reviewCategories={reviewCategories}
        platformData={platformData}
        skeletonData={skeletonData}
        id={id}
      />

      <hr className='text-[#EBEBEB] my-10 md:block hidden' />
      <FaqAccordion className='md:w-full' />
    </>
  )
}

export function DesktopBookingSidebar({
  id,
  initialGuests,
  initialCheckin,
  initialCheckout,
  propertiesDetailsData,
  propertiesDetailsLoading,
  showGuestsDropdown,
  setShowGuestsDropdown,
}) {
  return (
    <div
      className={`xl:flex-[40%] w-full ${showGuestsDropdown ? 'h-[670px]' : 'h-[390px]'} sticky top-[10px] left-[24px] gap-[25px] right-0 opacity-100 hidden min-md:block z-20`}
    >
      {propertiesDetailsLoading || !propertiesDetailsData ? (
        <Skeleton h={390} className='w-full rounded-xl z-50' bdrs={10} />
      ) : (
        <NewBookingForm
          propertyId={id}
          data={propertiesDetailsData?.data}
          initialGuests={initialGuests}
          initialCheckin={initialCheckin}
          initialCheckout={initialCheckout}
          showGuestsDropdown={showGuestsDropdown}
          setShowGuestsDropdown={setShowGuestsDropdown}
        />
      )}
    </div>
  )
}